import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Plus, Trash, UserCircle } from "@phosphor-icons/react";

const empty = { name: "", email: "", password: "", role: "staff" };

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [delId, setDelId] = useState(null);

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name || !form.email || !form.password) { toast.error("Tous les champs sont requis."); return; }
    if (form.password.length < 6) { toast.error("Mot de passe : min. 6 caractères."); return; }
    try { await api.post("/users", form); toast.success("Utilisateur créé"); setOpen(false); setForm(empty); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async () => { try { await api.delete(`/users/${delId}`); toast.success("Utilisateur supprimé"); setDelId(null); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tighter">Utilisateurs</h1>
          <p className="text-muted-foreground mt-1">Comptable, agent de bureau et autres membres du personnel.</p>
        </div>
        <Button data-testid="new-user-btn" className="rounded-sm" onClick={() => { setForm(empty); setOpen(true); }}><Plus size={18} className="mr-1" /> Nouvel utilisateur</Button>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-muted/50"><TableHead>Nom</TableHead><TableHead>Courriel</TableHead><TableHead>Rôle</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="text-muted-foreground">{u.email}</TableCell>
                <TableCell><span className={`text-xs px-2 py-0.5 rounded-sm border ${u.role === "admin" ? "bg-slate-900 text-white border-slate-900" : "bg-muted text-muted-foreground border-border"}`}>{u.role === "admin" ? "Administrateur" : "Personnel"}</span></TableCell>
                <TableCell className="text-right">
                  {u.id !== me?.id && (
                    <Button data-testid={`delete-user-${u.id}`} variant="ghost" size="icon" className="h-8 w-8 hover:text-destructive" onClick={() => setDelId(u.id)}><Trash size={16} /></Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><UserCircle size={20} /> Nouvel utilisateur</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <F label="Nom complet"><Input data-testid="user-name" className="rounded-sm" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></F>
            <F label="Courriel"><Input data-testid="user-email" type="email" className="rounded-sm" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></F>
            <F label="Mot de passe"><Input data-testid="user-password" type="text" className="rounded-sm" placeholder="min. 6 caractères" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></F>
            <F label="Rôle">
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role" className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Personnel (comptable, agent de bureau)</SelectItem>
                  <SelectItem value="admin">Administrateur (accès complet)</SelectItem>
                </SelectContent>
              </Select>
            </F>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Annuler</Button>
            <Button data-testid="save-user-btn" className="rounded-sm" onClick={save}>Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(v) => !v && setDelId(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader><AlertDialogTitle>Supprimer cet utilisateur ?</AlertDialogTitle><AlertDialogDescription>Cette personne ne pourra plus se connecter.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="rounded-sm">Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-user" className="rounded-sm bg-destructive hover:bg-destructive/90" onClick={del}>Supprimer</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function F({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{label}</Label>{children}</div>;
}
